import { useCallback } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiCardList,
  MiList,
  MiMenu,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  DATE_TIME_FORMATS,
  ICONS_WEB,
  PERMISSIONS,
} from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { ACCOUNTING_DEFAULT_FIELD_WIDTH } from "../../../accountingConstants";
import { formatOpenBalanceDisplay } from "../../reconcileConstants";

const BackchargeCard = ({ item, onClick, onEdit, onDelete }) => {
  const { classes, theme } = useStyles();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission(PERMISSIONS.CREDIT_MEMO.UPDATE);
  const canDelete = hasPermission(PERMISSIONS.CREDIT_MEMO.VOID);

  const sections = [
    {
      fieldsetStyle: {
        justifyContent: "flex-end",
        alignItems: "center",
      },
      fieldset: [
        {
          field: "name",
          maxWidth: 500,
          justifyContent: "flex-end",
          renderCell: ({ date, amount }) => {
            return (
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="flex-end"
                gap={theme.spacing(2)}
              >
                <MiStackedTypography
                  title="Date Added"
                  titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                  description={formatDate(
                    date,
                    DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                  )}
                  descriptionProps={{
                    width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                  }}
                />
                <MiStackedTypography
                  title="Amount"
                  titleProps={{ width: ACCOUNTING_DEFAULT_FIELD_WIDTH }}
                  description={formatOpenBalanceDisplay(-amount)}
                  descriptionProps={{
                    width: ACCOUNTING_DEFAULT_FIELD_WIDTH,
                    style: {
                      color: theme.palette.red.main,
                    },
                  }}
                />
              </Box>
            );
          },
        },
      ],
    },
  ];

  const renderTitle = (backcharge) => (
    <MiStackedTypography
      title="Backcharge"
      description={`#${backcharge?.poNumber || ""}`}
      titleProps={{
        className: classes.fieldDescription,
        color: "primary.main",
        width: 300,
      }}
      descriptionProps={{
        width: 300,
        color: "text.secondary",
      }}
    />
  );

  const renderItemActions = (backcharge) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <MiMenu
        data={[
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdi[ICONS_WEB.PENCIL_OUTLINE],
            disabled: !canUpdate,
            action: () => onEdit(backcharge),
          },
          {
            label: BUTTON_LABELS.VOID,
            icon: mdi[ICONS_WEB.CANCEL],
            disabled: !canDelete,
            action: () => onDelete(backcharge),
            color: theme.palette.red.main,
          },
        ]}
      />
    </Box>
  );

  return (
    <MiCardList
      item={item}
      containerStyles={{ padding: 0, marginTop: theme.spacing(2) }}
      sections={sections}
      leftCardProps={{
        leftMaxWidth: 300,
      }}
      renderTitle={renderTitle}
      renderItemActions={renderItemActions}
      onClick={onClick}
    />
  );
};

export const BackchargesList = ({
  backcharges = [],
  onClick,
  onEdit,
  onDelete,
}) => {
  const { classes } = useStyles();
  const totalAmount = backcharges.reduce(
    (sum, bc) => sum + (bc?.amount || 0),
    0
  );

  const CardComponent = useCallback(
    ({ item }) => (
      <BackchargeCard
        item={item}
        onClick={onClick}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    ),
    [onClick, onEdit, onDelete]
  );

  return (
    <>
      <Box display="flex" gap={4} p={2} pt={1}>
        <MiStackedTypography
          title="Backcharges"
          description={backcharges?.length}
          descriptionProps={{
            className: classes.fieldDescription,
          }}
        />
        <MiStackedTypography
          title="Total Amount"
          description={formatOpenBalanceDisplay(-totalAmount)}
          descriptionProps={{
            className: classes.fieldDescription,
            color: "red.main",
          }}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        sx={{ overflow: "hidden" }}
      >
        <MiList
          hideListPagination
          viewType="card"
          CardComponent={CardComponent}
          data={backcharges}
        />
      </Box>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  fieldDescription: {
    maxWidth: 110,
    fontWeight: theme.fontWeight.bold,
  },
}));
